import wormholeWaterImg from 'assets/images/jpeg_3_69661aaeec.jpg';
import portalTexture1 from 'assets/images/png_5_379f0d1d39.png';
import portalTexture2 from 'assets/images/png_6_8218af0b8d.png';
import portalTexture3 from 'assets/images/png_7_5dbeeed96d.png';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// --- Portal constants (match b7.html) ---
const PORTAL_POS = new THREE.Vector3(250, 50, 280);
const INNER_PORTAL_RADIUS = 430;

const TEXTURE1_URL = portalTexture1;
const TEXTURE2_URL = portalTexture2;
const TEXTURE3_URL = portalTexture3;

// --- Wormhole constants ---
const APPROACH_DIST = 55;
const THROUGH_DIST = 10;
const ALIGN_DURATION = 0.55;
const SUCK_DURATION = 0.75;
const TRAVEL_DURATION = 3.5;
const RETURN_DURATION = 0.9;
const WORMHOLE_START_AT = 0.68;
const WORMHOLE_COLOR = 0x55d7ff;
const WORMHOLE_LIGHT_COLOR = 0x77e6ff;

type TravelPhase = 'idle' | 'align' | 'suck' | 'travel' | 'return';

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Per-instance state for InstancedMesh layers
interface LayerState {
  mesh: THREE.InstancedMesh;
  count: number;
  px: Float32Array;
  py: Float32Array;
  pz: Float32Array;
  sx: Float32Array;
  sy: Float32Array;
  rz: Float32Array;
}

// --- Portal particle system data ---
interface PortalParticleData {
  // InstancedMesh layers (high particle count, uniform opacity)
  layer1: LayerState | null;
  layer2: LayerState | null;
  layer3: LayerState | null;
  smoke2: LayerState | null;
  smoke3: LayerState | null;
  backSmoke: LayerState | null;
  // Keep as Mesh[] for per-particle opacity animation
  smokeParticles1: THREE.Mesh[];
  logoEmitters?: THREE.Mesh[];
  portalLight: THREE.PointLight;
  coreGlow: THREE.Mesh;
  emitterGlow: THREE.Mesh;
}

function makeLayerState(count: number): Omit<LayerState, 'mesh'> {
  return {
    count,
    px: new Float32Array(count),
    py: new Float32Array(count),
    pz: new Float32Array(count),
    sx: new Float32Array(count).fill(1),
    sy: new Float32Array(count).fill(1),
    rz: new Float32Array(count),
  };
}

// Shared dummy for matrix composition
const _dummy = new THREE.Object3D();

function applyLayerMatrix(layer: LayerState, i: number): void {
  _dummy.position.set(layer.px[i], layer.py[i], layer.pz[i]);
  _dummy.rotation.set(0, 0, layer.rz[i]);
  _dummy.scale.set(layer.sx[i], layer.sy[i], 1);
  _dummy.updateMatrix();
  layer.mesh.setMatrixAt(i, _dummy.matrix);
}

function flushLayer(layer: LayerState | null): void {
  if (!layer) return;
  layer.mesh.instanceMatrix.needsUpdate = true;
}

// --- Portal animation updater ---
class PortalAnimator {
  private ps: PortalParticleData | null;
  private clock1 = new THREE.Clock();
  private clock2 = new THREE.Clock();
  private clock3 = new THREE.Clock();
  private _lightInit = false;
  private _flickerBasePower = 350;
  private _flickerRangePower = 500;

  constructor(private group: THREE.Group) {
    const ud = group?.userData?.portalSystem;
    this.ps = ud || null;
  }

  update(cam: THREE.PerspectiveCamera, elapsed: number): void {
    if (!this.group || !this.ps) return;
    const ps = this.ps;

    const d1 = Math.min(this.clock1.getDelta(), 0.05);
    const d2 = Math.min(this.clock2.getDelta(), 0.05);
    const d3 = Math.min(this.clock3.getDelta(), 0.05);

    // Layer 1 particles (InstancedMesh)
    if (ps.layer1) {
      const l = ps.layer1;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d1 * 0.5;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Smoke layer 1: per-particle position + opacity (keep as Mesh[])
    ps.smokeParticles1.forEach((p) => {
      p.rotation.z -= d1 * 0.35;

      const ud = p.userData as Record<string, number>;
      if (ud._angle === undefined) ud._angle = Math.atan2(p.position.y, p.position.x);
      if (ud._baseRadius === undefined) {
        const r0 = Math.sqrt(p.position.x * p.position.x + p.position.y * p.position.y);
        ud._baseRadius = r0;
        if (ud._radius === undefined) ud._radius = r0;
      }
      if (ud._seed === undefined) ud._seed = Math.random() * Math.PI * 2;
      if (ud.baseOpacity === undefined) {
        const mat = p.material as THREE.MeshBasicMaterial;
        ud.baseOpacity = mat.opacity !== undefined ? mat.opacity : 0.16;
      }

      const t = elapsed;
      ud._angle += d1 * (0.85 + 0.25 * Math.sin(t * 0.9 + ud._seed));
      ud._radius -= d1 * (32.0 + 10.0 * Math.sin(t * 0.7 + ud._seed * 1.7));

      const inner = Math.max(300, ud._baseRadius - 160);
      const outer = ud._baseRadius;
      if (ud._radius < inner) ud._radius = outer;

      const billow =
        1.0 + 0.4 * Math.sin(t * 1.3 + ud._seed) + 0.15 * Math.sin(t * 2.7 + ud._seed * 0.7);
      p.scale.set(billow, billow, 1);

      const mat = p.material as THREE.MeshBasicMaterial;
      if (mat.opacity !== undefined) {
        mat.opacity = ud.baseOpacity * (0.62 + 0.38 * Math.sin(t * 1.1 + ud._seed * 2.0));
      }

      p.position.x = Math.cos(ud._angle) * ud._radius;
      p.position.y = Math.sin(ud._angle) * ud._radius;
    });

    // Layer 2 (InstancedMesh)
    if (ps.layer2) {
      const l = ps.layer2;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d2 * 0.3;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Smoke layer 2 (InstancedMesh)
    if (ps.smoke2) {
      const l = ps.smoke2;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d2 * 0.1;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Layer 3 (InstancedMesh)
    if (ps.layer3) {
      const l = ps.layer3;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d3 * 0.3;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Smoke layer 3 (InstancedMesh)
    if (ps.smoke3) {
      const l = ps.smoke3;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d3 * 0.1;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Back smoke (InstancedMesh)
    if (ps.backSmoke) {
      const l = ps.backSmoke;
      for (let i = 0; i < l.count; i++) {
        l.rz[i] -= d1 * 0.15;
        applyLayerMatrix(l, i);
      }
      flushLayer(l);
    }

    // Light flicker
    if (ps.portalLight) {
      if (!this._lightInit) {
        const currentPower =
          ps.portalLight.power !== undefined
            ? ps.portalLight.power
            : ps.portalLight.intensity !== undefined
              ? ps.portalLight.intensity * (4 * Math.PI)
              : 500;
        this._flickerBasePower = Math.max(50, currentPower * 0.7);
        this._flickerRangePower = Math.max(50, currentPower * 1.0);
        this._lightInit = true;
      }

      if (Math.random() > 0.9) {
        const pwr = this._flickerBasePower + Math.random() * this._flickerRangePower;
        this.setLightPower(ps.portalLight, pwr);
      }
    }

    // Glow pulse
    const t = elapsed;
    const pulse = 0.55 + 0.45 * Math.sin(t * 1.35);
    const pulse2 = 0.7 + 0.3 * Math.sin(t * 2.1 + 1.2);

    if (ps.coreGlow?.material) {
      const mat = ps.coreGlow.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.86 + 0.12 * pulse;
      const s = 1.0 + 0.06 * pulse2;
      ps.coreGlow.scale.set(s, s, 1);
    }

    if (ps.emitterGlow?.material) {
      const mat = ps.emitterGlow.material as THREE.MeshBasicMaterial;
      mat.opacity = 0.7 + 0.3 * pulse2;
      const s2 = 1.0 + 0.1 * pulse;
      ps.emitterGlow.scale.set(s2, s2, 1);
    }

    // Logo emitters pulse (keep as Mesh[] for per-instance opacity)
    if (ps.logoEmitters?.length) {
      const glowPulse = 0.65 + 0.35 * Math.sin(t * 1.55);
      ps.logoEmitters.forEach((e) => {
        if (!e?.material) return;
        const mat = e.material as THREE.MeshBasicMaterial;
        const ph = (e.userData as Record<string, number>)?._phase || 0;
        const local = 0.7 + 0.3 * Math.sin(t * 2.25 + ph);
        mat.opacity = 0.55 + 0.55 * glowPulse * local;
        const sc = 0.92 + 0.2 * glowPulse * local;
        e.scale.set(sc, sc, 1);
      });
    }

    // Smooth light breathing
    if (ps.portalLight) {
      const base = this._flickerBasePower || 350;
      const range = this._flickerRangePower || 500;
      const smooth = base + range * (0.35 + 0.65 * pulse);
      this.setLightPower(ps.portalLight, smooth);
    }

    // Face camera
    if (cam?.position) this.group.lookAt(cam.position);
  }

  private setLightPower(light: THREE.PointLight, power: number): void {
    if (light.power !== undefined) {
      light.power = power;
    } else if (light.intensity !== undefined) {
      light.intensity = power / (4 * Math.PI);
    }
  }
}

// --- Create portal (exact port from b6.html) ---
function createIntergalacticPortal(portalScene: THREE.Scene): THREE.Group {
  const group = new THREE.Group();
  group.position.copy(PORTAL_POS);
  group.scale.setScalar(0.03);

  const smokeParticles1: THREE.Mesh[] = [];

  // Back smoke group
  const backSmokeGroup = new THREE.Group();
  backSmokeGroup.position.z = -50;
  group.add(backSmokeGroup);

  // Lights
  const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
  dirLight.position.set(0, 0, 1);
  group.add(dirLight);

  const portalLight = new THREE.PointLight(0x1e90ff, 85, 1600, 1.0);
  portalLight.position.set(0, 0, 10);
  group.add(portalLight);

  // --- NEAR logo emitters ---
  const logoLightGroup = new THREE.Group();
  logoLightGroup.name = 'logoLightGroup';
  logoLightGroup.position.set(0, 0, 32);
  group.add(logoLightGroup);

  const logoGlowCanvas = document.createElement('canvas');
  logoGlowCanvas.width = 128;
  logoGlowCanvas.height = 128;
  const lg = logoGlowCanvas.getContext('2d')!;
  const lgrd = lg.createRadialGradient(64, 64, 0, 64, 64, 64);
  lgrd.addColorStop(0.0, 'rgba(120,235,255,1.00)');
  lgrd.addColorStop(0.25, 'rgba(30,144,255,0.95)');
  lgrd.addColorStop(0.55, 'rgba(20,90,190,0.35)');
  lgrd.addColorStop(1.0, 'rgba(0,0,0,0.0)');
  lg.clearRect(0, 0, 128, 128);
  lg.fillStyle = lgrd;
  lg.fillRect(0, 0, 128, 128);
  const logoGlowTex = new THREE.CanvasTexture(logoGlowCanvas);
  if (logoGlowTex.colorSpace !== undefined) logoGlowTex.colorSpace = THREE.SRGBColorSpace;
  logoGlowTex.needsUpdate = true;

  // Badge shape points
  const nearLogoPoints: [number, number][] = [];
  const pts = 28;
  const rx = 0.5,
    ry = 0.14;
  const cr = 0.12;
  for (let i = 0; i < pts; i++) {
    const t = i / (pts - 1);
    nearLogoPoints.push([-rx + cr + 2 * (rx - cr) * t, ry]);
  }
  for (let i = 0; i < Math.floor(pts / 2); i++) {
    const t = i / (Math.floor(pts / 2) - 1);
    nearLogoPoints.push([rx, ry - cr - 2 * (ry - cr) * t]);
  }
  for (let i = 0; i < pts; i++) {
    const t = i / (pts - 1);
    nearLogoPoints.push([rx - cr - 2 * (rx - cr) * t, -ry]);
  }
  for (let i = 0; i < Math.floor(pts / 2); i++) {
    const t = i / (Math.floor(pts / 2) - 1);
    nearLogoPoints.push([-rx, -ry + cr + 2 * (ry - cr) * t]);
  }

  const badgeScaleX = INNER_PORTAL_RADIUS * 0.95;
  const badgeScaleY = INNER_PORTAL_RADIUS * 0.28;
  const logoEmitters: THREE.Mesh[] = [];
  for (let i = 0; i < nearLogoPoints.length; i++) {
    const [nx, ny] = nearLogoPoints[i];
    const mat = new THREE.MeshBasicMaterial({
      map: logoGlowTex,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });
    const g = new THREE.PlaneGeometry(85, 85);
    const m = new THREE.Mesh(g, mat);
    m.position.set(nx * badgeScaleX, ny * badgeScaleY, 0);
    m.renderOrder = 8;
    m.userData._phase = (i / nearLogoPoints.length) * Math.PI * 2;
    logoLightGroup.add(m);
    logoEmitters.push(m);
  }

  group.userData.portalSystem = group.userData.portalSystem || {};
  group.userData.portalSystem.logoLightGroup = logoLightGroup;
  group.userData.portalSystem.logoEmitters = logoEmitters;

  // --- Core glow ---
  const glowCanvas = document.createElement('canvas');
  glowCanvas.width = 256;
  glowCanvas.height = 256;
  const gctx = glowCanvas.getContext('2d')!;
  const grd = gctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grd.addColorStop(0.0, 'rgba(120, 235, 255, 1.0)');
  grd.addColorStop(0.25, 'rgba(30, 144, 255, 0.95)');
  grd.addColorStop(0.55, 'rgba(20, 90, 190, 0.35)');
  grd.addColorStop(0.8, 'rgba(0, 0, 0, 0.0)');
  grd.addColorStop(1.0, 'rgba(0, 0, 0, 0.0)');
  gctx.clearRect(0, 0, 256, 256);
  gctx.fillStyle = grd;
  gctx.fillRect(0, 0, 256, 256);

  const glowTex = new THREE.CanvasTexture(glowCanvas);
  if (glowTex.colorSpace !== undefined) glowTex.colorSpace = THREE.SRGBColorSpace;
  glowTex.needsUpdate = true;

  const coreGlowGeo = new THREE.CircleGeometry(INNER_PORTAL_RADIUS, 96);
  const coreGlowMat = new THREE.MeshBasicMaterial({
    map: glowTex,
    transparent: true,
    opacity: 0.88,
    blending: THREE.NormalBlending,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
  });
  const coreGlow = new THREE.Mesh(coreGlowGeo, coreGlowMat);
  coreGlow.position.set(0, 0, 20);
  coreGlow.renderOrder = 5;
  group.add(coreGlow);

  // --- Emitter glow ---
  const emitterCanvas = document.createElement('canvas');
  emitterCanvas.width = 256;
  emitterCanvas.height = 256;
  const ectx = emitterCanvas.getContext('2d')!;
  const grad = ectx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0.0, 'rgba(80, 220, 255, 1.0)');
  grad.addColorStop(0.22, 'rgba(40, 150, 255, 0.95)');
  grad.addColorStop(0.55, 'rgba(20, 90, 255, 0.45)');
  grad.addColorStop(1.0, 'rgba(0, 0, 0, 0.0)');
  ectx.fillStyle = grad;
  ectx.fillRect(0, 0, 256, 256);

  const emitterTex = new THREE.CanvasTexture(emitterCanvas);
  if (emitterTex.colorSpace !== undefined) emitterTex.colorSpace = THREE.SRGBColorSpace;
  emitterTex.needsUpdate = true;

  const emitterGeo = new THREE.CircleGeometry(INNER_PORTAL_RADIUS * 0.78, 96);
  const emitterMat = new THREE.MeshBasicMaterial({
    map: emitterTex,
    transparent: true,
    opacity: 0.9,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
  });
  const emitterGlow = new THREE.Mesh(emitterGeo, emitterMat);
  emitterGlow.position.set(0, 0, 28);
  emitterGlow.renderOrder = 6;
  group.add(emitterGlow);

  // --- Particle layers from textures ---
  const loader = new THREE.TextureLoader();

  // LAYER 1 - GRAY - OUTER EDGE (180 instances)
  loader.load(TEXTURE1_URL, (texture1) => {
    if (texture1.colorSpace !== undefined) texture1.colorSpace = THREE.SRGBColorSpace;
    texture1.needsUpdate = true;

    const geo1 = new THREE.PlaneGeometry(350, 350);
    const mat1 = new THREE.MeshBasicMaterial({
      map: texture1,
      color: 0xffffff,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
      blending: THREE.NormalBlending,
      opacity: 1,
    });

    const count1 = 880 - 700; // 180
    const mesh1 = new THREE.InstancedMesh(geo1, mat1, count1);
    mesh1.renderOrder = 30;
    mesh1.frustumCulled = false;
    const state1 = makeLayerState(count1) as LayerState;
    state1.mesh = mesh1;

    let idx = 0;
    for (let p = 880; p > 700; p--) {
      const x = 0.62 * p * Math.cos((4 * p * Math.PI) / 180);
      const y = 0.62 * p * Math.sin((4 * p * Math.PI) / 180);
      const rz = Math.random() * Math.PI * 2;
      state1.px[idx] = x;
      state1.py[idx] = y;
      state1.pz[idx] = 0;
      state1.sx[idx] = 1;
      state1.sy[idx] = 1;
      state1.rz[idx] = rz;
      applyLayerMatrix(state1, idx);
      idx++;
    }
    mesh1.instanceMatrix.needsUpdate = true;
    group.add(mesh1);

    // Smoke layer 1 - per-particle opacity: keep as Mesh[]
    const smokeGeo1 = new THREE.PlaneGeometry(700, 900);
    for (let p = 0; p < 48; p++) {
      const smoke = new THREE.Mesh(smokeGeo1, mat1.clone());
      const angle = Math.random() * Math.PI * 2;
      const radius = 380 + Math.random() * 80;
      smoke.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
      smoke.userData._seed = Math.random() * Math.PI * 2;
      smoke.userData._basePos = smoke.position.clone();
      smoke.userData._angle = Math.atan2(smoke.position.y, smoke.position.x);
      smoke.userData._radius = Math.sqrt(
        smoke.position.x * smoke.position.x + smoke.position.y * smoke.position.y
      );
      smoke.rotation.z = Math.random() * Math.PI * 2;
      (smoke.material as THREE.MeshBasicMaterial).opacity = 0.14;
      smoke.userData.baseOpacity = 0.14;
      (smoke.material as THREE.MeshBasicMaterial).toneMapped = false;
      (smoke.material as THREE.MeshBasicMaterial).depthWrite = false;
      (smoke.material as THREE.MeshBasicMaterial).depthTest = true;
      smoke.renderOrder = 20;
      smokeParticles1.push(smoke);
      group.add(smoke);
    }

    // Store state into portalSystem
    const ps = group.userData.portalSystem as PortalParticleData;
    ps.layer1 = state1;
    ps.smokeParticles1 = smokeParticles1;

    // (no layer assignment needed — portal is in separate portalScene)
  });

  // LAYER 2 - BLUE - MIDDLE (600 instances + 80 smoke instances)
  loader.load(TEXTURE2_URL, (texture2) => {
    if (texture2.colorSpace !== undefined) texture2.colorSpace = THREE.SRGBColorSpace;
    texture2.needsUpdate = true;

    const geo2 = new THREE.PlaneGeometry(350, 350);
    const mat2 = new THREE.MeshLambertMaterial({
      map: texture2,
      transparent: true,
      color: 0xffffff,
      opacity: 0.72,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });

    const count2 = 800 - 200; // 600
    const mesh2 = new THREE.InstancedMesh(geo2, mat2, count2);
    mesh2.renderOrder = 40;
    mesh2.frustumCulled = false;
    const state2 = makeLayerState(count2) as LayerState;
    state2.mesh = mesh2;

    let idx2 = 0;
    for (let p = 800; p > 200; p--) {
      const x = 0.48 * p * Math.cos((4 * p * Math.PI) / 180);
      const y = 0.48 * p * Math.sin((4 * p * Math.PI) / 180);
      const r = Math.sqrt(x * x + y * y);
      const maxHalf = Math.max(30, INNER_PORTAL_RADIUS - r);
      const s = Math.min(2.0, (maxHalf * 2) / 350);
      const rz = Math.random() * Math.PI * 2;
      state2.px[idx2] = x;
      state2.py[idx2] = y;
      state2.pz[idx2] = 0;
      state2.sx[idx2] = s;
      state2.sy[idx2] = s;
      state2.rz[idx2] = rz;
      applyLayerMatrix(state2, idx2);
      idx2++;
    }
    mesh2.instanceMatrix.needsUpdate = true;
    group.add(mesh2);

    // Smoke layer 2 (InstancedMesh, uniform opacity 0.12, rotation-only)
    const smokeGeo2 = new THREE.PlaneGeometry(700, 900);
    const smokeMat2 = new THREE.MeshLambertMaterial({
      map: texture2,
      transparent: true,
      color: 0xffffff,
      opacity: 0.12,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });
    const smoke2Count = 80;
    const meshSmoke2 = new THREE.InstancedMesh(smokeGeo2, smokeMat2, smoke2Count);
    meshSmoke2.renderOrder = 39;
    meshSmoke2.frustumCulled = false;
    const stateSmoke2 = makeLayerState(smoke2Count) as LayerState;
    stateSmoke2.mesh = meshSmoke2;

    for (let p = 0; p < smoke2Count; p++) {
      const x = Math.random() * 500 - 250;
      const y = Math.random() * 300 - 150;
      const rz = Math.random() * Math.PI * 2;
      stateSmoke2.px[p] = x;
      stateSmoke2.py[p] = y;
      stateSmoke2.pz[p] = 0;
      stateSmoke2.sx[p] = 1;
      stateSmoke2.sy[p] = 1;
      stateSmoke2.rz[p] = rz;
      applyLayerMatrix(stateSmoke2, p);
    }
    meshSmoke2.instanceMatrix.needsUpdate = true;
    group.add(meshSmoke2);

    const ps = group.userData.portalSystem as PortalParticleData;
    ps.layer2 = state2;
    ps.smoke2 = stateSmoke2;

    // (no layer assignment needed — portal is in separate portalScene)
  });

  // LAYER 3 - CYAN - CENTER (450 instances + 70 smoke instances)
  loader.load(TEXTURE3_URL, (texture3) => {
    if (texture3.colorSpace !== undefined) texture3.colorSpace = THREE.SRGBColorSpace;
    texture3.needsUpdate = true;

    const geo3 = new THREE.PlaneGeometry(350, 350);
    const mat3 = new THREE.MeshLambertMaterial({
      map: texture3,
      transparent: true,
      color: 0xffffff,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });

    const count3 = 650 - 200; // 450
    const mesh3 = new THREE.InstancedMesh(geo3, mat3, count3);
    mesh3.renderOrder = 45;
    mesh3.frustumCulled = false;
    const state3 = makeLayerState(count3) as LayerState;
    state3.mesh = mesh3;

    let idx3 = 0;
    for (let p = 650; p > 200; p--) {
      const x = 0.4 * p * Math.cos((4 * p * Math.PI) / 180);
      const y = 0.4 * p * Math.sin((4 * p * Math.PI) / 180);
      const rz = Math.random() * Math.PI * 2;
      state3.px[idx3] = x;
      state3.py[idx3] = y;
      state3.pz[idx3] = 0;
      state3.sx[idx3] = 1;
      state3.sy[idx3] = 1;
      state3.rz[idx3] = rz;
      applyLayerMatrix(state3, idx3);
      idx3++;
    }
    mesh3.instanceMatrix.needsUpdate = true;
    group.add(mesh3);

    // Smoke layer 3 (InstancedMesh, uniform opacity 0.28, rotation-only)
    const smokeGeo3 = new THREE.PlaneGeometry(600, 500);
    const smokeMat3 = new THREE.MeshLambertMaterial({
      map: texture3,
      transparent: true,
      color: 0xffffff,
      opacity: 0.28,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
    });
    const smoke3Count = 70;
    const meshSmoke3 = new THREE.InstancedMesh(smokeGeo3, smokeMat3, smoke3Count);
    meshSmoke3.renderOrder = 44;
    meshSmoke3.frustumCulled = false;
    const stateSmoke3 = makeLayerState(smoke3Count) as LayerState;
    stateSmoke3.mesh = meshSmoke3;

    for (let p = 0; p < smoke3Count; p++) {
      const x = Math.random() * 400 - 200;
      const y = Math.random() * 280 - 140;
      const rz = Math.random() * Math.PI * 2;
      stateSmoke3.px[p] = x;
      stateSmoke3.py[p] = y;
      stateSmoke3.pz[p] = 0;
      stateSmoke3.sx[p] = 1;
      stateSmoke3.sy[p] = 1;
      stateSmoke3.rz[p] = rz;
      applyLayerMatrix(stateSmoke3, p);
    }
    meshSmoke3.instanceMatrix.needsUpdate = true;
    group.add(meshSmoke3);

    // Back smoke (InstancedMesh, uniform opacity 0.14, rotation-only)
    const backSmokeGeo = new THREE.PlaneGeometry(1000, 800);
    const backSmokeMat = new THREE.MeshBasicMaterial({
      map: texture3,
      transparent: true,
      color: 0xffffff,
      blending: THREE.NormalBlending,
      depthWrite: false,
      opacity: 0.14,
      toneMapped: false,
    });
    const backSmokeCount = 80;
    const meshBack = new THREE.InstancedMesh(backSmokeGeo, backSmokeMat, backSmokeCount);
    meshBack.renderOrder = 10;
    meshBack.frustumCulled = false;
    backSmokeGroup.add(meshBack);
    const stateBack = makeLayerState(backSmokeCount) as LayerState;
    stateBack.mesh = meshBack;

    for (let p = 0; p < backSmokeCount; p++) {
      const x = Math.random() * 1000 - 500;
      const y = Math.random() * 400 - 200;
      const z = Math.random() * 50;
      const rz = Math.random() * Math.PI * 2;
      stateBack.px[p] = x;
      stateBack.py[p] = y;
      stateBack.pz[p] = z;
      stateBack.sx[p] = 1;
      stateBack.sy[p] = 1;
      stateBack.rz[p] = rz;
      applyLayerMatrix(stateBack, p);
    }
    meshBack.instanceMatrix.needsUpdate = true;

    const ps = group.userData.portalSystem as PortalParticleData;
    ps.layer3 = state3;
    ps.smoke3 = stateSmoke3;
    ps.backSmoke = stateBack;

    // (no layer assignment needed — portal is in separate portalScene)
  });

  // Initialize portalSystem data
  group.userData.portalSystem = {
    ...group.userData.portalSystem,
    layer1: null,
    layer2: null,
    layer3: null,
    smoke2: null,
    smoke3: null,
    backSmoke: null,
    smokeParticles1,
    portalLight,
    coreGlow,
    emitterGlow,
  };

  portalScene.add(group);
  return group;
}

/**
 * Portal (separate scene, rendered by main renderer) + wormhole travel.
 * Ported from: usePortal.ts, useWormholeTravel.ts
 * Portal rendered after depth restore so it is correctly occluded by the ship.
 */
export class PortalSystem {
  private portalGroup: THREE.Group;
  private portalAnimator: PortalAnimator;
  private portalScene: THREE.Scene;
  private portalElapsed = 0;

  // Fly animation
  private flyAnimId: number | null = null;
  private savedCamPos: THREE.Vector3 | null = null;
  private savedCamTarget: THREE.Vector3 | null = null;

  // Wormhole
  private wormholeOverlay: {
    group: THREE.Object3D;
    material: THREE.MeshBasicMaterial;
    texture: THREE.Texture;
  } | null = null;
  private travelState = {
    phase: 'idle' as TravelPhase,
    t: 0,
    elapsed: 0,
    wormholeStarted: false,
    ready: false,
    visible: false,
    hyper: false,
    fade: 0.0,
    targetFade: 0.0,
    slow: 1.0,
    scale: 1.0,
    normal_speed: 0.004,
    speed: 10.0,
    upRatio: 1.06,
    upScale: 1.06,
    downRatio: 1.85,
    downScale: 1.85,
    startPos: new THREE.Vector3(),
    stagePos: new THREE.Vector3(),
    throughPos: new THREE.Vector3(),
    returnStartPos: new THREE.Vector3(),
    returnDestPos: new THREE.Vector3(),
    travelNormal: new THREE.Vector3(),
    savedClearColor: new THREE.Color(),
    savedClearAlpha: 1,
    savedAutoRotate: false,
  };

  // Callbacks
  private onArrivedAtPortal: (() => void) | null = null;
  private onReturnedFromPortal: (() => void) | null = null;
  private onAtPortalChanged: ((atPortal: boolean) => void) | null = null;
  private onTravelComplete: (() => void) | null = null;

  // Track which objects were hidden before wormhole travel (to restore correctly)
  private hiddenBeforeTravel = new Set<THREE.Object3D>();

  constructor(
    private container: HTMLDivElement,
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls,
    private mainScene: THREE.Scene,
    private mainRenderer: THREE.WebGLRenderer,
    portalScene: THREE.Scene
  ) {
    this.portalScene = portalScene;
    // Create portal in separate portal scene (rendered by main renderer after depth restore)
    this.portalGroup = createIntergalacticPortal(this.portalScene);
    this.portalGroup.lookAt(camera.position);
    this.portalGroup.updateMatrixWorld(true);
    this.portalAnimator = new PortalAnimator(this.portalGroup);

    // Create wormhole overlay
    this.createWormholeOverlay();
  }

  setCallbacks(
    onArrived: () => void,
    onReturned: () => void,
    onAtPortalChanged: (atPortal: boolean) => void,
    onTravelComplete?: () => void
  ): void {
    this.onArrivedAtPortal = onArrived;
    this.onReturnedFromPortal = onReturned;
    this.onAtPortalChanged = onAtPortalChanged;
    this.onTravelComplete = onTravelComplete || null;
  }

  private createWormholeOverlay(): void {
    const group = new THREE.Object3D();
    group.visible = false;
    group.frustumCulled = false;
    group.renderOrder = 9999;

    const loader = new THREE.TextureLoader();
    const texture = loader.load(
      wormholeWaterImg,
      () => {
        this.travelState.ready = true;
      },
      undefined,
      () => {
        /* keep not-ready */
      }
    );
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    texture.anisotropy = this.mainRenderer.capabilities?.getMaxAnisotropy?.() || 1;

    const geo = new THREE.CylinderGeometry(12, 0, 40, 48, 48, true);
    const material = new THREE.MeshBasicMaterial({
      color: WORMHOLE_COLOR,
      opacity: 0.0,
      map: texture,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    const cylinder = new THREE.Mesh(geo, material);
    cylinder.rotation.x = Math.PI / 2;
    group.add(cylinder);

    const light = new THREE.PointLight(WORMHOLE_LIGHT_COLOR, 4, 100);
    light.position.set(0, 0, 0);
    group.add(light);

    this.mainScene.add(group);
    this.wormholeOverlay = { group, material, texture };
  }

  tick(dt: number): void {
    this.portalElapsed += dt;

    // Distance-based optimization
    const distToPortal = this.camera.position.distanceTo(PORTAL_POS);
    const isAtPortal = this.travelState.phase !== 'idle';
    if (distToPortal < 250 || isAtPortal || this.flyAnimId !== null) {
      this.portalAnimator.update(this.camera, this.portalElapsed);
    }

    // Animate wormhole travel
    this.animateTravel(dt);
  }

  // --- Fly to portal ---
  flyToPortal(): void {
    if (this.flyAnimId) {
      cancelAnimationFrame(this.flyAnimId);
      this.flyAnimId = null;
    }

    this.savedCamPos = this.camera.position.clone();
    this.savedCamTarget = this.controls.target.clone();

    this.controls.autoRotate = false;
    this.controls.enabled = false;

    this.onAtPortalChanged?.(true);

    const portalPos = PORTAL_POS.clone();
    const startTarget = this.controls.target.clone();
    const endTarget = portalPos.clone();
    const startPos = this.camera.position.clone();

    const dirToCenter = new THREE.Vector3(-250, -50, -280).normalize();
    const portalDistance = 50;
    const endPos = new THREE.Vector3(
      portalPos.x + dirToCenter.x * portalDistance,
      portalPos.y + dirToCenter.y * portalDistance + 5,
      portalPos.z + dirToCenter.z * portalDistance
    );

    const tmpMat = new THREE.Matrix4();
    const lookQuat = (from: THREE.Vector3, to: THREE.Vector3) => {
      tmpMat.lookAt(from, to, this.camera.up);
      return new THREE.Quaternion().setFromRotationMatrix(tmpMat);
    };

    const startQuat = this.camera.quaternion.clone();
    const endQuat = lookQuat(startPos, endTarget);

    const rotateDuration = 850;
    const flyDuration = 1250;
    const rotateStartTime = performance.now();
    let flyStartTime = 0;
    let didStartFly = false;

    const animateToPortal = () => {
      const now = performance.now();

      if (!didStartFly) {
        const t = Math.min((now - rotateStartTime) / rotateDuration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        this.controls.target.lerpVectors(startTarget, endTarget, eased);
        this.camera.position.copy(startPos);
        this.camera.quaternion.slerpQuaternions(startQuat, endQuat, eased);

        if (t < 1) {
          this.flyAnimId = requestAnimationFrame(animateToPortal);
          return;
        }

        didStartFly = true;
        this.controls.target.copy(endTarget);
        flyStartTime = performance.now();
      }

      const t = Math.min((now - flyStartTime) / flyDuration, 1);
      const eased = 1 - Math.pow(1 - t, 3);

      this.controls.target.copy(endTarget);
      this.camera.position.lerpVectors(startPos, endPos, eased);
      this.camera.lookAt(endTarget);

      if (t < 1) {
        this.flyAnimId = requestAnimationFrame(animateToPortal);
      } else {
        this.flyAnimId = null;
        this.controls.enabled = true;
        this.controls.maxDistance = 2500;
        this.controls.minDistance = 5;
        this.onArrivedAtPortal?.();
      }
    };

    animateToPortal();
  }

  // --- Fly back ---
  flyBack(): void {
    if (this.flyAnimId) {
      cancelAnimationFrame(this.flyAnimId);
      this.flyAnimId = null;
    }

    this.controls.autoRotate = false;
    this.controls.enabled = false;

    this.onAtPortalChanged?.(false);

    const startTarget = this.controls.target.clone();
    const startPos = this.camera.position.clone();
    const endTarget = (this.savedCamTarget || new THREE.Vector3(0, 0, 0)).clone();
    const endPos = (this.savedCamPos || new THREE.Vector3(0, 40, 80)).clone();

    const tmpMat = new THREE.Matrix4();
    const lookQuat = (from: THREE.Vector3, to: THREE.Vector3) => {
      tmpMat.lookAt(from, to, this.camera.up);
      return new THREE.Quaternion().setFromRotationMatrix(tmpMat);
    };

    const startQuat = this.camera.quaternion.clone();
    const endQuat = lookQuat(startPos, endTarget);

    const rotateDuration = 800;
    const flyDuration = 1200;
    const rotateStartTime = performance.now();
    let flyStartTime = 0;
    let didStartFly = false;

    const animateBack = () => {
      const now = performance.now();

      if (!didStartFly) {
        const t = Math.min((now - rotateStartTime) / rotateDuration, 1);
        const eased = 1 - Math.pow(1 - t, 3);

        this.controls.target.lerpVectors(startTarget, endTarget, eased);
        this.camera.position.copy(startPos);
        this.camera.quaternion.slerpQuaternions(startQuat, endQuat, eased);

        if (t < 1) {
          this.flyAnimId = requestAnimationFrame(animateBack);
          return;
        }

        didStartFly = true;
        this.controls.target.copy(endTarget);
        flyStartTime = performance.now();
      }

      const t = Math.min((now - flyStartTime) / flyDuration, 1);
      const eased = 1 - Math.pow(1 - t, 3);

      this.controls.target.copy(endTarget);
      this.camera.position.lerpVectors(startPos, endPos, eased);
      this.camera.lookAt(endTarget);

      if (t < 1) {
        this.flyAnimId = requestAnimationFrame(animateBack);
      } else {
        this.flyAnimId = null;
        this.controls.enabled = true;
        this.controls.autoRotate = true;
        this.controls.maxDistance = 1500;
        this.controls.minDistance = 5;
        this.onReturnedFromPortal?.();
      }
    };

    animateBack();
  }

  // --- Wormhole travel ---
  startTravel(): void {
    const s = this.travelState;
    if (s.phase !== 'idle') return;

    s.savedAutoRotate = this.controls.autoRotate;
    this.controls.autoRotate = false;
    this.controls.enableDamping = false;
    this.controls.enabled = false;

    const portalPos = PORTAL_POS.clone();
    s.travelNormal.set(-portalPos.x, -portalPos.y, -portalPos.z).normalize();
    s.startPos.copy(this.camera.position);

    const distToPortal = this.camera.position.distanceTo(portalPos);
    if (distToPortal < APPROACH_DIST * 1.25) {
      s.stagePos.copy(this.camera.position);
    } else {
      s.stagePos.copy(portalPos).addScaledVector(s.travelNormal, APPROACH_DIST);
    }

    s.throughPos.copy(portalPos).addScaledVector(s.travelNormal, -THROUGH_DIST);

    s.phase = 'align';
    s.t = 0;
    s.elapsed = 0;
    s.wormholeStarted = false;
  }

  stopTravel(immediate = false): void {
    const s = this.travelState;
    if (s.phase === 'idle') return;

    if (immediate) {
      s.phase = 'idle';
      this.wormholeStop();

      if (this.wormholeOverlay) {
        this.wormholeOverlay.group.visible = false;
        s.fade = 0;
        s.targetFade = 0;
      }

      this.setWorldVisible(true);
      this.mainRenderer.setClearColor(s.savedClearColor, s.savedClearAlpha);

      this.controls.enabled = true;
      this.controls.autoRotate = true;
      this.controls.enableDamping = true;

      this.onAtPortalChanged?.(false);
      return;
    }

    // Smooth return
    s.returnStartPos.copy(this.camera.position);
    s.returnDestPos.copy(s.stagePos);
    s.phase = 'return';
    s.t = 0;
    this.wormholeStop();

    // Restore world immediately so stars are visible through fading wormhole
    this.setWorldVisible(true);
    this.mainRenderer.setClearColor(s.savedClearColor, s.savedClearAlpha);
  }

  private animateTravel(dt: number): void {
    const s = this.travelState;
    if (s.phase === 'idle') return;

    this.updateWormholeOverlay(dt);

    const portalPos = PORTAL_POS;

    // ALIGN
    if (s.phase === 'align') {
      s.t = Math.min(1, s.t + dt / ALIGN_DURATION);
      const e = easeInOutCubic(s.t);

      this.camera.position.lerpVectors(s.startPos, s.stagePos, e);
      this.camera.lookAt(portalPos);
      this.controls.target.copy(portalPos);

      if (s.t >= 1) {
        s.phase = 'suck';
        s.t = 0;
      }
      return;
    }

    // SUCK
    if (s.phase === 'suck') {
      s.t = Math.min(1, s.t + dt / SUCK_DURATION);
      const e = easeInOutCubic(s.t);

      this.camera.position.lerpVectors(s.stagePos, s.throughPos, e);

      const lookTarget = this.camera.position.clone().addScaledVector(s.travelNormal, -50);
      this.camera.lookAt(lookTarget);
      this.controls.target.copy(lookTarget);

      if (!s.wormholeStarted && s.t >= WORMHOLE_START_AT) {
        s.wormholeStarted = true;
        this.wormholeStart();

        this.mainRenderer.getClearColor(s.savedClearColor);
        s.savedClearAlpha = this.mainRenderer.getClearAlpha();
        this.mainRenderer.setClearColor(0x000000, 1);

        this.setWorldVisible(false);
      }

      if (s.t >= 1) {
        if (!s.wormholeStarted) {
          s.wormholeStarted = true;
          this.wormholeStart();
          this.mainRenderer.getClearColor(s.savedClearColor);
          s.savedClearAlpha = this.mainRenderer.getClearAlpha();
          this.mainRenderer.setClearColor(0x000000, 1);
          this.setWorldVisible(false);
        }

        s.phase = 'travel';
        s.t = 0;
        s.elapsed = 0;
      }
      return;
    }

    // TRAVEL
    if (s.phase === 'travel') {
      s.elapsed += dt;
      this.camera.translateZ(-dt * 3.5);

      if (s.elapsed >= TRAVEL_DURATION) {
        s.returnStartPos.copy(this.camera.position);
        s.returnDestPos.copy(s.stagePos);
        s.phase = 'return';
        s.t = 0;
        this.wormholeStop();

        // Restore world immediately so stars are visible through fading wormhole
        this.setWorldVisible(true);
        this.mainRenderer.setClearColor(s.savedClearColor, s.savedClearAlpha);
      }
      return;
    }

    // RETURN
    if (s.phase === 'return') {
      s.t = Math.min(1, s.t + dt / RETURN_DURATION);
      const e = easeInOutCubic(s.t);

      this.camera.position.lerpVectors(s.returnStartPos, s.returnDestPos, e);

      const lookTarget = this.camera.position.clone().addScaledVector(s.travelNormal, 100);
      this.camera.lookAt(lookTarget);
      this.controls.target.copy(lookTarget);

      if (s.t >= 1) {
        s.phase = 'idle';

        this.controls.enabled = true;
        this.controls.autoRotate = true;
        this.controls.enableDamping = true;

        if (this.wormholeOverlay) {
          this.wormholeOverlay.group.visible = false;
          s.fade = 0;
          s.targetFade = 0;
        }

        // World already restored at return start (TRAVEL→RETURN transition)

        this.onAtPortalChanged?.(false);
        this.controls.target.set(0, 0, 0);
        this.controls.update();

        // Smoothly return camera to default position
        this.onTravelComplete?.();
      }
    }
  }

  private wormholeStart(): void {
    const overlay = this.wormholeOverlay;
    if (!overlay) return;

    const s = this.travelState;
    s.hyper = true;
    s.visible = true;
    s.fade = 1.0;
    s.targetFade = 0.9;
    s.slow = 1.0;
    s.scale = 1.0;
    overlay.material.opacity = 0.9;
    overlay.group.visible = true;

    if (overlay.texture) {
      overlay.texture.offset.set(0, 0);
      overlay.texture.needsUpdate = true;
    }
  }

  private wormholeStop(): void {
    this.travelState.hyper = false;
    this.travelState.targetFade = 0.0;
  }

  private updateWormholeOverlay(dt: number): void {
    const overlay = this.wormholeOverlay;
    if (!overlay) return;

    const s = this.travelState;

    // Smooth fade
    s.fade += (s.targetFade - s.fade) * Math.min(1, dt * 12.0);
    overlay.material.opacity = Math.max(0.0, Math.min(1.0, s.fade));

    if (s.fade < 0.005 && s.targetFade <= 0.0) {
      s.visible = false;
      overlay.group.visible = false;
      return;
    } else {
      overlay.group.visible = true;
    }

    // Pin to camera (POV)
    overlay.group.position.copy(this.camera.position);
    overlay.group.quaternion.copy(this.camera.quaternion);
    overlay.group.rotation.z -= 0.008;

    const tex = overlay.texture;
    if (!tex) return;

    if (s.hyper) {
      s.slow = s.slow >= s.speed ? s.speed : s.slow * s.upRatio;
      s.scale = s.scale <= 0.1 ? 0.1 : s.scale / s.upScale;

      tex.offset.y -= s.normal_speed * s.slow;
      tex.needsUpdate = true;

      overlay.group.scale.set(s.scale, s.scale, 1);
    } else {
      s.slow = s.slow <= 1 ? 1 : s.slow / s.downRatio;
      s.scale = s.scale >= 1 ? 1 : s.scale * s.downScale;
      overlay.group.scale.set(s.scale, s.scale, 1);
    }
  }

  private setWorldVisible(visible: boolean): void {
    if (!visible) {
      // Save which objects were already hidden (scanner beam, etc.)
      this.hiddenBeforeTravel.clear();
      this.mainScene.children.forEach((child) => {
        if (this.wormholeOverlay && child === this.wormholeOverlay.group) return;
        if (!child.visible) this.hiddenBeforeTravel.add(child);
        child.visible = false;
      });
    } else {
      // Restore — skip objects that were already hidden before travel
      this.mainScene.children.forEach((child) => {
        if (this.wormholeOverlay && child === this.wormholeOverlay.group) return;
        if (this.hiddenBeforeTravel.has(child)) return;
        child.visible = true;
      });
      this.hiddenBeforeTravel.clear();
    }
  }

  destroy(): void {
    if (this.flyAnimId) {
      cancelAnimationFrame(this.flyAnimId);
    }

    // Wormhole overlay cleanup
    if (this.wormholeOverlay) {
      this.mainScene.remove(this.wormholeOverlay.group);
      this.wormholeOverlay.material.dispose();
      if (this.wormholeOverlay.texture instanceof THREE.Texture) {
        this.wormholeOverlay.texture.dispose();
      }
    }

    // Portal group cleanup
    if (this.portalGroup) {
      this.portalScene.remove(this.portalGroup);
      this.portalGroup.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          mesh.geometry.dispose();
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((m) => m.dispose());
        }
      });
    }
  }
}
